/**
 * AudiencesPanel Component
 * Displays and allows editing of audience targeting for a Demand Gen ad group
 *
 * Features:
 * - View current audience segments (User Lists, Custom Audiences, etc.)
 * - Add new audiences via AudienceSelector modal
 * - Remove audiences with confirmation
 * - View demographic targeting (read-only for now)
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Badge } from '@/components/ui';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import type { AdGroup, Campaign, AudienceSegment } from '@/types/campaign';
import { campaignsApi } from '@/services/api/campaigns';
import { AudienceSelector } from '@/components/features/AudienceTargeting/AudienceSelector';
import { formatEnumPretty } from '@/utils/formatEnum';

interface AudiencesPanelProps {
  adGroup: AdGroup;
  campaign: Campaign;
}

// Helper to format audience type for display
function formatAudienceType(type: string): string {
  const typeMap: Record<string, string> = {
    'USER_LIST': 'User List',
    'AUDIENCE': 'Audience',
    'CUSTOM_AUDIENCE': 'Custom Audience',
    'COMBINED_AUDIENCE': 'Combined Audience',
  };
  return typeMap[type] || formatEnumPretty(type);
}

// Helper to get badge color for audience type
function getAudienceTypeColor(type: string, isNegative: boolean): string {
  if (isNegative) return 'bg-red-100 text-red-800 border-red-200';

  const colorMap: Record<string, string> = {
    'USER_LIST': 'bg-blue-100 text-blue-800 border-blue-200',
    'AUDIENCE': 'bg-purple-100 text-purple-800 border-purple-200',
    'CUSTOM_AUDIENCE': 'bg-indigo-100 text-indigo-800 border-indigo-200',
    'COMBINED_AUDIENCE': 'bg-violet-100 text-violet-800 border-violet-200',
  };
  return colorMap[type] || 'bg-gray-100 text-gray-800 border-gray-200';
}

export function AudiencesPanel({ adGroup, campaign }: AudiencesPanelProps) {
  const [showSelector, setShowSelector] = useState(false);
  const [audienceToRemove, setAudienceToRemove] = useState<AudienceSegment | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get current audiences from campaign.audience_targeting
  const currentAudiences = useMemo(() => {
    const targeting = campaign.audience_targeting;
    if (!targeting) return [];

    const adGroupAudience = targeting.ad_group_audiences.find(
      ag => ag.ad_group_id === adGroup.ad_group_id
    );
    return adGroupAudience?.audiences || [];
  }, [campaign.audience_targeting, adGroup.ad_group_id]);

  // Get demographics from campaign.audience_targeting
  const demographics = useMemo(() => {
    const targeting = campaign.audience_targeting;
    if (!targeting) return [];
    return targeting.demographics || [];
  }, [campaign.audience_targeting]);

  // Handle adding audiences (uses batch API for efficiency)
  const handleAddAudiences = useCallback(async (selectedAudiences: Array<{ resource_name: string; audience_name: string }>) => {
    setError(null);

    try {
      // Use batch API to add all audiences in a single request
      const result = await campaignsApi.addAudiencesToAdGroup(
        campaign.campaign_id,
        adGroup.ad_group_id,
        selectedAudiences.map(audience => ({
          audience_resource_name: audience.resource_name
        }))
      );

      if (result.failed_count > 0) {
        setError(`Added ${result.added_count} audiences, but ${result.failed_count} failed.`);
      }

      setShowSelector(false);
      // Page will refresh via data sync
    } catch (err) {
      console.error('Failed to add audiences:', err);
      setError('Failed to add audiences. Please try again.');
    }
  }, [campaign.campaign_id, adGroup.ad_group_id]);

  // Handle removing an audience
  const handleRemoveAudience = useCallback(async () => {
    if (!audienceToRemove) return;

    setIsRemoving(true);
    setError(null);

    try {
      await campaignsApi.removeAudienceFromAdGroup(
        campaign.campaign_id,
        adGroup.ad_group_id,
        audienceToRemove.criterion_id
      );
      setAudienceToRemove(null);
      // Page will refresh via data sync
    } catch (err) {
      console.error('Failed to remove audience:', err);
      setError('Failed to remove audience. Please try again.');
    } finally {
      setIsRemoving(false);
    }
  }, [campaign.campaign_id, adGroup.ad_group_id, audienceToRemove]);

  // Get excluded audience IDs (for USER_LIST types that have user_list_id)
  const excludedAudienceIds = useMemo(() => {
    return currentAudiences
      .map(a => a.user_list_id)
      .filter((id): id is string => id != null);
  }, [currentAudiences]);

  // Get excluded audience names (for Custom/Combined audiences without IDs)
  const excludedAudienceNames = useMemo(() => {
    return currentAudiences
      .map(a => a.audience_name)
      .filter((name): name is string => name != null);
  }, [currentAudiences]);

  return (
    <div className="p-4 space-y-6">
      {/* Error message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Audience Segments Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-900">
            Audience Segments
            {currentAudiences.length > 0 && (
              <span className="ml-2 text-gray-500 font-normal">({currentAudiences.length})</span>
            )}
          </h4>
          <button
            onClick={() => setShowSelector(true)}
            className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
          >
            + Add Audience
          </button>
        </div>

        {currentAudiences.length === 0 ? (
          <div className="text-center py-6 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-600 text-sm">No audiences targeted</p>
            <p className="text-gray-500 text-xs mt-1">Add audiences to reach specific user segments</p>
          </div>
        ) : (
          <div className="space-y-2">
            {currentAudiences.map((audience) => (
              <div
                key={audience.criterion_id}
                className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Badge
                    className={`flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded border ${getAudienceTypeColor(audience.audience_type, audience.is_negative)}`}
                  >
                    {formatAudienceType(audience.audience_type)}
                  </Badge>
                  <span className="text-sm text-gray-900 truncate" title={audience.audience_name || undefined}>
                    {audience.audience_name || `Audience ${audience.criterion_id}`}
                  </span>
                  {audience.is_negative && (
                    <span className="text-xs text-red-600 font-medium">(Excluded)</span>
                  )}
                </div>
                <button
                  onClick={() => setAudienceToRemove(audience)}
                  className="flex-shrink-0 p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
                  title="Remove audience"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Demographics Section (Read-only) */}
      {demographics.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            Demographic Targeting
            <span className="ml-2 text-gray-500 font-normal">({demographics.length})</span>
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {demographics.map((demo) => (
              <div
                key={demo.criterion_id}
                className={`px-3 py-2 text-sm rounded-lg border ${
                  demo.is_negative
                    ? 'bg-red-50 border-red-200 text-red-800'
                    : 'bg-gray-50 border-gray-200 text-gray-700'
                }`}
              >
                <span className="text-xs text-gray-500 block">
                  {formatAudienceType(demo.audience_type)}
                </span>
                <span className="font-medium">
                  {demo.audience_name || demo.criterion_id}
                </span>
                {demo.is_negative && (
                  <span className="text-xs text-red-600 ml-1">(Excluded)</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Audience Selector Modal */}
      {showSelector && (
        <AudienceSelector
          campaignId={campaign.campaign_id}
          onSelect={handleAddAudiences}
          onClose={() => setShowSelector(false)}
          excludedAudienceIds={excludedAudienceIds}
          excludedAudienceNames={excludedAudienceNames}
        />
      )}

      {/* Remove Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!audienceToRemove}
        onClose={() => setAudienceToRemove(null)}
        onConfirm={handleRemoveAudience}
        title="Remove Audience"
        message={`Are you sure you want to remove "${audienceToRemove?.audience_name || 'this audience'}" from this ad group?`}
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
        isLoading={isRemoving}
      />
    </div>
  );
}
